/**
 * The body of the "Scheduled run" dialog (TRA-1475): when a team's GRPO run
 * starts on its own, and what it trains.
 *
 * It holds the draft and nothing else. The dialog around it reads, saves
 * and starts a run, and remounts this when the saved schedule changes, so
 * the fields always start from what is saved.
 */

import { useState } from 'react'

import type {
  TrainingGrpoOptionsResponse,
  TrainingScheduleResponse,
  TrainingScheduleUpdate,
} from '@/api/generated/model'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { DialogFooter } from '@/components/ui/dialog'
import { FieldError } from '@/components/ui/field-error'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'

import { GRPO_STEPS, grpoDatasetSummary } from './launch'
import {
  WEEKDAYS,
  browserTimeZone,
  datasetNote,
  isScheduleChanged,
  lastRunLine,
  nextRunLine,
  parseSchedule,
  scheduleDraft,
} from './schedule'
import type { NewestDataset, ScheduleDraft, ScheduleErrors, ScheduleField, Weekday } from './schedule'
import { PlannedStart } from './PlannedStart'
import { PresetField } from './PresetField'
import { ValidationDatasetsField } from './ValidationDatasetsField'

interface ScheduledRunFormProps {
  data: TrainingScheduleResponse
  grpo: TrainingGrpoOptionsResponse
  isSaving: boolean
  /** A "Run now" start is in flight. */
  isStarting: boolean
  onCancel: () => void
  onSave: (update: TrainingScheduleUpdate) => void
  onRunNow: () => void
}

const EYEBROW = 'font-mono text-[10px] uppercase tracking-wider text-muted-foreground'
/** The picker's value for "the team's newest proven dataset". A colon is in no registry id. */
const NEWEST = ':newest'

/** A saved schedule's next run, where that run starts and the last run it started. */
function Status({ data }: { data: TrainingScheduleResponse }): React.JSX.Element | null {
  if (data.schedule === null) return null
  return (
    <dl className="grid grid-cols-1 gap-3 border border-border p-3 sm:grid-cols-2">
      <div className="space-y-1">
        <dt className={EYEBROW}>Next run</dt>
        <dd className="text-sm text-foreground">{nextRunLine(data.schedule)}</dd>
      </div>
      <div className="space-y-1">
        <dt className={EYEBROW}>Last run</dt>
        <dd className="font-mono text-xs text-foreground">{lastRunLine(data.last_run)}</dd>
      </div>
      <div className="space-y-1 sm:col-span-2">
        <dt className={EYEBROW}>Starts from</dt>
        <dd>
          <PlannedStart data={data} />
        </dd>
      </div>
    </dl>
  )
}

function DaysField({
  days,
  disabled,
  error,
  onChange,
}: {
  days: Weekday[]
  disabled: boolean
  error: string | undefined
  onChange: (days: Weekday[]) => void
}): React.JSX.Element {
  return (
    <fieldset className="space-y-2" aria-describedby={error === undefined ? undefined : 'schedule-days-error'}>
      <legend className="text-sm font-medium">Days</legend>
      <div className="flex flex-wrap gap-x-4 gap-y-2">
        {WEEKDAYS.map((day) => {
          const id = `schedule-day-${day.id}`
          return (
            <div key={day.id} className="flex items-center gap-2">
              <Checkbox
                id={id}
                checked={days.includes(day.id)}
                disabled={disabled}
                onCheckedChange={(checked) => {
                  onChange(
                    checked === true ? [...days, day.id] : days.filter((other) => other !== day.id),
                  )
                }}
              />
              <Label htmlFor={id} className="font-normal">
                {day.label}
              </Label>
            </div>
          )
        })}
      </div>
      <FieldError id="schedule-days-error" message={error} />
    </fieldset>
  )
}

function DatasetField({
  grpo,
  dataset,
  newest,
  disabled,
  onChange,
}: {
  grpo: TrainingGrpoOptionsResponse
  dataset: string | null
  newest: NewestDataset
  disabled: boolean
  onChange: (dataset: string | null) => void
}): React.JSX.Element {
  const note = datasetNote(dataset, newest)
  // A pinned dataset that left the registry still shows as what is saved.
  const pinnedGone = dataset !== null && !grpo.datasets.some((option) => option.registry_id === dataset)
  return (
    <div className="space-y-2">
      <Label htmlFor="schedule-dataset">Dataset</Label>
      <Select
        value={dataset ?? NEWEST}
        disabled={disabled}
        onValueChange={(next) => {
          onChange(next === NEWEST ? null : next)
        }}
      >
        <SelectTrigger id="schedule-dataset" aria-describedby="schedule-dataset-note">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={NEWEST}>Newest proven dataset</SelectItem>
          {pinnedGone && (
            <SelectItem value={dataset}>
              <span className="font-mono text-xs">{dataset}</span>
            </SelectItem>
          )}
          {grpo.datasets.map((option) => (
            <SelectItem key={option.registry_id} value={option.registry_id}>
              <span className="flex items-baseline gap-2">
                <span className="font-mono text-xs">{option.registry_id}</span>
                <span className={EYEBROW}>{grpoDatasetSummary(option)}</span>
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p
        id="schedule-dataset-note"
        className={note.missing ? 'text-xs text-destructive' : 'text-xs text-muted-foreground'}
      >
        {note.text}
      </p>
    </div>
  )
}

/** One labelled text input with its help line and its error. */
function TextField({
  field,
  label,
  help,
  value,
  error,
  disabled,
  type = 'text',
  mono = false,
  placeholder,
  onChange,
}: {
  field: ScheduleField
  label: string
  help: string
  value: string
  error: string | undefined
  disabled: boolean
  type?: 'text' | 'time' | 'number'
  mono?: boolean
  placeholder?: string
  onChange: (value: string) => void
}): React.JSX.Element {
  const id = `schedule-${field}`
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        type={type}
        className={mono ? 'font-mono text-xs' : undefined}
        placeholder={placeholder}
        value={value}
        disabled={disabled}
        aria-invalid={error !== undefined}
        aria-describedby={error === undefined ? `${id}-help` : `${id}-error`}
        onChange={(event) => {
          onChange(event.target.value)
        }}
      />
      {/* The error takes the help line's place: it says what to enter too. */}
      {error === undefined && (
        <p id={`${id}-help`} className="text-xs text-muted-foreground">
          {help}
        </p>
      )}
      <FieldError id={`${id}-error`} message={error} />
    </div>
  )
}

export function ScheduledRunForm({
  data,
  grpo,
  isSaving,
  isStarting,
  onCancel,
  onSave,
  onRunNow,
}: ScheduledRunFormProps): React.JSX.Element {
  const [draft, setDraft] = useState<ScheduleDraft>(() =>
    scheduleDraft(data.schedule, { preset: grpo.default_preset, timeZone: browserTimeZone() }),
  )
  const [errors, setErrors] = useState<ScheduleErrors>({})
  const busy = isSaving || isStarting
  const changed = isScheduleChanged(draft, data.schedule)
  const set = <K extends keyof ScheduleDraft>(key: K, value: ScheduleDraft[K]): void => {
    setDraft((current) => ({ ...current, [key]: value }))
  }

  return (
    <form
      className="space-y-4"
      noValidate
      onSubmit={(event) => {
        event.preventDefault()
        const parsed = parseSchedule(draft)
        setErrors(parsed.kind === 'error' ? parsed.errors : {})
        if (parsed.kind === 'ok') onSave(parsed.update)
      }}
    >
      <Status data={data} />
      <DaysField
        days={draft.weekdays}
        disabled={busy}
        error={errors.weekdays}
        onChange={(days) => {
          set('weekdays', days)
        }}
      />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <TextField
          field="localTime"
          label="Time"
          help="The run starts at this time in the time zone you enter."
          type="time"
          value={draft.localTime}
          error={errors.localTime}
          disabled={busy}
          onChange={(value) => {
            set('localTime', value)
          }}
        />
        <TextField
          field="timeZone"
          label="Time zone"
          help="Enter an IANA name such as America/Los_Angeles."
          mono
          value={draft.timeZone}
          error={errors.timeZone}
          disabled={busy}
          onChange={(value) => {
            set('timeZone', value)
          }}
        />
      </div>
      <DatasetField
        grpo={grpo}
        dataset={draft.dataset}
        newest={{ id: data.newest_proven_dataset, problem: data.registry_problem }}
        disabled={busy}
        onChange={(dataset) => {
          set('dataset', dataset)
        }}
      />
      {/* Shown for a saved dataset the registry no longer lists too: it
          has to be cleared here before an enabled schedule saves again. */}
      {(grpo.validation_datasets.length > 0 || draft.validationIds.length > 0) && (
        <ValidationDatasetsField
          options={grpo.validation_datasets}
          selected={draft.validationIds}
          disabled={busy}
          onChange={(ids) => {
            set('validationIds', ids)
          }}
        />
      )}
      <TextField
        field="checkpoint"
        label="Starting checkpoint"
        help="Leave this empty to start each run from the team's best earlier adapter. A team with none starts a fresh adapter."
        mono
        placeholder="training-runs/team/run/exports/adapters/step_5/"
        value={draft.checkpoint}
        error={errors.checkpoint}
        disabled={busy}
        onChange={(value) => {
          set('checkpoint', value)
        }}
      />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <PresetField
          id="schedule-preset"
          presets={grpo.presets}
          value={draft.preset}
          error={errors.preset}
          disabled={busy}
          onChange={(value) => {
            set('preset', value)
          }}
        />
        <TextField
          field="stepsText"
          label="Optimizer steps"
          help={`Enter ${String(GRPO_STEPS.min)} to ${String(GRPO_STEPS.max)}. A run ends early when it reaches the preset's epoch limit.`}
          type="number"
          value={draft.stepsText}
          error={errors.stepsText}
          disabled={busy}
          onChange={(value) => {
            set('stepsText', value)
          }}
        />
      </div>
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <Checkbox
            id="schedule-enabled"
            checked={draft.enabled}
            disabled={busy}
            aria-describedby="schedule-enabled-help"
            onCheckedChange={(checked) => {
              set('enabled', checked === true)
            }}
          />
          <Label htmlFor="schedule-enabled" className="font-normal">
            Run on this schedule
          </Label>
        </div>
        <p id="schedule-enabled-help" className="text-xs text-muted-foreground">
          Clear this to pause the timer and keep the schedule.
        </p>
      </div>
      {data.schedule !== null && changed && (
        <p className="text-xs text-muted-foreground">Save your changes before you start a run.</p>
      )}
      <DialogFooter>
        {data.schedule !== null && (
          <Button
            type="button"
            variant="outline"
            className="sm:mr-auto"
            disabled={busy || changed}
            onClick={onRunNow}
          >
            Run now
          </Button>
        )}
        <Button type="button" variant="outline" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        <Button type="submit" disabled={busy || !changed}>
          {isSaving ? 'Saving...' : 'Save schedule'}
        </Button>
      </DialogFooter>
    </form>
  )
}
